import * as React from 'react';
import { DELETED_ENTITY_CLASS, useDeletedEntity } from '@/lib/useDeletedEntity';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import type { System } from '@bliks-io/contracts';
import { Item, ItemMedia, ItemContent, ItemTitle, ItemDescription, cn } from '@bliks-io/ui';

interface SystemSuccessorItemProps {
  system: System;
  variant?: 'default' | 'outline' | 'muted';
}

/**
 * SystemSuccessorItem Component
 *
 * Displays a system successor using the v2 Item component.
 * Shows system logo (if available) or system icon, name, and optional description.
 * Soft-deleted systems render grayed out with a "(deleted)" suffix.
 */
export const SystemSuccessorItem = React.forwardRef<HTMLDivElement, SystemSuccessorItemProps>(
  ({ system, variant = 'outline' }, ref) => {
    const { isDeleted, deletedSuffix } = useDeletedEntity(system);
    let logoUrl: string | undefined;
    if (system.logo) {
      if (system.logo.type === 'svg') {
        logoUrl = system.logo.svg;
      } else {
        logoUrl = system.logo.thumbnail?.webp || system.logo.thumbnail?.png;
      }
    }

    return (
      <Item
        ref={ref}
        variant={variant}
        size="default"
        className={cn('pr-8', isDeleted && DELETED_ENTITY_CLASS)}
      >
        <ItemMedia variant={logoUrl ? 'image' : 'icon'}>
          {logoUrl ? (
            <img src={logoUrl} alt={system.name} className="h-10 w-10 object-contain" />
          ) : (
            <ENTITY_ICONS.system className="h-4 w-4" />
          )}
        </ItemMedia>
        <ItemContent>
          <ItemTitle>{system.name + deletedSuffix}</ItemTitle>
          {system.description && <ItemDescription>{system.description}</ItemDescription>}
        </ItemContent>
      </Item>
    );
  }
);

SystemSuccessorItem.displayName = 'SystemSuccessorItem';
